import { Injectable } from '@angular/core';

/**
 * Tiene in cache le route accessibili dall'utente corrente, estratte dal menu
 * sidebar caricato via `PortalService.getMenuItems()`.
 *
 * Usato dal `WidgetSelectorDialogComponent` per filtrare le voci del catalogo
 * widget (`WIDGET_CATALOG`) in base ai permessi dell'utente — coerente con il
 * sistema permessi pagine già esistente nel portale (tabella `prt_role_page`,
 * SP `prt_sidebar_menu_get`).
 *
 * Pattern: la sidebar component, dopo aver caricato il menu via HTTP, chiama
 * `loadFromMenu(menuItems)` per popolare la cache. Da quel momento qualunque
 * altro componente può chiamare `canAccessRoute(route)` o `accessibleRoutes`.
 */
@Injectable({ providedIn: 'root' })
export class UserAccessibleRoutesService {
	/** Insieme normalizzato delle route accessibili (es. '/fatture/lista', '/companies') */
	private routes: Set<string> = new Set();
	/** True dopo la prima chiamata a `loadFromMenu`, anche se il menu era vuoto */
	private loaded = false;

	/**
	 * Popola la cache estraendo ricorsivamente le `url` da un menu sidebar.
	 * Sostituisce qualunque stato precedente (re-login / cambio company).
	 * Marca la cache come "caricata" anche se il menu è vuoto (utente senza permessi).
	 */
	loadFromMenu(menuItems: any[] | null | undefined): void {
		this.routes.clear();
		this.loaded = true;
		if (!menuItems || !menuItems.length) return;
		this.collect(menuItems);
	}

	/**
	 * Svuota la cache e la riporta allo stato "non caricato" (boot phase fail-open).
	 * Va chiamato al logout: la cache è un singleton root che sopravvive alla
	 * navigazione SPA, altrimenti l'utente successivo erediterebbe le route del
	 * precedente finché la sidebar non ricarica il menu.
	 */
	reset(): void {
		this.routes.clear();
		this.loaded = false;
	}

	private collect(items: any[]): void {
		for (const item of items) {
			if (item && typeof item.url === 'string' && item.url.trim()) {
				this.routes.add(this.normalize(item.url));
			}
			// Il menu sidebar del portale usa il campo `submenu` per i children
			// (vedi app-sidebar-nav-item template — `item.submenu ? true : false`)
			const children = (item && (item.submenu || item.children)) as any[] | undefined;
			if (Array.isArray(children) && children.length) {
				this.collect(children);
			}
		}
	}

	/**
	 * Verifica se l'utente ha accesso a una determinata route.
	 *
	 * Una route è "accessibile" se almeno una delle route del menu utente
	 * è prefisso della route richiesta (dopo normalizzazione dei `:params`).
	 *
	 * Comportamento "fail-open" SOLO finché la cache non è stata caricata almeno
	 * una volta (boot phase). Dopo `loadFromMenu`, se la cache è vuota significa
	 * che l'utente NON ha permessi e tutti i widget catalog vengono nascosti.
	 *
	 * Esempi:
	 * - canAccessRoute('/fatture/lista/:cmp_id') ritorna true se nel menu c'è '/fatture/lista' o '/fatture'
	 * - canAccessRoute(undefined) ritorna true (no constraint)
	 * - canAccessRoute('/x/y') prima di loadFromMenu → true (fail-open boot)
	 * - canAccessRoute('/x/y') dopo loadFromMenu([]) → false (utente senza permessi)
	 */
	canAccessRoute(route: string | undefined | null): boolean {
		if (!route) return true;
		if (!this.loaded) return true; // fail-open: cache non ancora caricata
		if (!this.routes.size) return false; // caricato vuoto = utente senza permessi
		const target = this.normalize(route);
		if (this.routes.has(target)) return true;
		for (const accessible of this.routes) {
			if (target.startsWith(accessible + '/') || accessible.startsWith(target + '/')) {
				return true;
			}
		}
		return false;
	}

	/** Tutte le route accessibili (per debug / introspezione) */
	get accessibleRoutes(): string[] {
		return Array.from(this.routes);
	}

	/** True se `loadFromMenu` è stata chiamata almeno una volta (anche con menu vuoto) */
	get isLoaded(): boolean {
		return this.loaded;
	}

	/**
	 * Normalizza una route per il matching:
	 * - rimuove eventuali `:cmp_id`, `:id`, ecc. (segmenti con `:`)
	 * - rimuove slash finale e doppi slash
	 * - aggiunge slash iniziale se mancante
	 *
	 * Es. `'fatture/lista/:cmp_id'` → `'/fatture/lista'`
	 */
	private normalize(route: string): string {
		let r = route.trim();
		if (!r.startsWith('/')) r = '/' + r;
		// Taglio al primo segmento `:param`
		const idx = r.indexOf('/:');
		if (idx >= 0) r = r.substring(0, idx);
		// Pulizia slash multipli e finale
		r = r.replace(/\/+/g, '/');
		if (r.length > 1 && r.endsWith('/')) r = r.slice(0, -1);
		return r;
	}
}
